import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { Loader2, Copy, CheckCircle2, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import type { TelegramStep, TelegramLinkData } from '../hooks/useTelegramLink';

interface Props {
  step: TelegramStep;
  linkData: TelegramLinkData | null;
  onStart: () => void;
  onRetry: () => void;
}

// Telegram SVG icon — matches the WhatsApp icon pattern used across the codebase
const TelegramIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.699 1.203-1.201 1.22-.506.017-1.267-.323-2.028-.823-1.077-.709-1.744-1.127-2.671-1.737-.991-.65-1.731-1.002-2.477-1.465-.746-.462-1.401-1.002-1.34-1.34.03-.166.471-.62.91-1.05.44-.43 2.502-2.316 2.553-2.528.05-.21-.013-.306-.118-.306-.104 0-1.895 1.189-3.238 2.083-.19.125-.395.187-.584.218-.19.03-.497.087-.803.05-.306-.038-.891-.186-1.258-.337-.367-.15-.658-.23-.623-.487.035-.257.377-.52.713-.706 2.766-1.528 5.679-2.88 8.761-4.045 1.05-.397 2.112-.767 3.167-1.053.491-.133.916-.179 1.25-.133.334.045.545.185.748.455.056.074.116.27.051.52zm-3.69 11.233z" />
  </svg>
);

// Three bouncing dots — visual "waiting" indicator
const WaitingDots = () => (
  <span className="flex gap-1 items-center" aria-hidden="true">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </span>
);

export const TelegramLinkPanel: React.FC<Props> = ({ step, linkData, onStart, onRetry }) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; if (copyTimer.current) clearTimeout(copyTimer.current); };
  }, []);

  const handleCopy = async () => {
    if (!linkData?.pin) return;
    let success = false;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(linkData.pin);
      success = true;
    } catch {
      const el = document.createElement('textarea');
      el.value = linkData.pin;
      el.style.position = 'fixed'; el.style.opacity = '0';
      document.body.appendChild(el);
      try { el.select(); success = document.execCommand('copy'); }
      catch { /* The visible selectable PIN remains available for manual copy. */ }
      finally { el.remove(); }
    }
    if (mounted.current && success) {
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    }
  };

  // Format PIN as "482 915" for readability (group of 3)
  const pin = linkData?.pin ?? '';
  const formattedPin = pin.length >= 4 ? `${pin.slice(0, 3)}\u2009${pin.slice(3)}` : pin;

  // ── IDLE: just the entry-point button ────────────────────────────────────
  if (step === 'idle') {
    return (
      <button
        type="button"
        onClick={onStart}
        className="w-full flex justify-center items-center gap-2 px-4 py-3 bg-blue-500 text-white text-sm font-bold rounded-xl hover:bg-blue-600 transition-colors"
      >
        <TelegramIcon className="w-4 h-4" />
        {t('profile.telegramLinkBtn')}
      </button>
    );
  }

  // ── GENERATING: spinner while fetching link + PIN from backend ────────────
  if (step === 'generating') {
    return (
      <div className="flex items-center justify-center gap-2 py-5 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
        {t('profile.telegramLinking')}
      </div>
    );
  }

  // ── ERROR ────────────────────────────────────────────────────────────────
  if (step === 'error') {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl text-sm border border-red-100">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {t('profile.telegramLinkError')}
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="w-full flex justify-center items-center gap-2 px-4 py-3 bg-blue-500 text-white text-sm font-bold rounded-xl hover:bg-blue-600 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          {t('profile.telegramRetry')}
        </button>
      </div>
    );
  }

  // ── TIMEOUT ───────────────────────────────────────────────────────────────
  if (step === 'timeout') {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 p-3 bg-amber-50 text-amber-700 rounded-xl text-sm border border-amber-100">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {t('profile.telegramTimeout')}
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="w-full flex justify-center items-center gap-2 px-4 py-3 bg-blue-500 text-white text-sm font-bold rounded-xl hover:bg-blue-600 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          {t('profile.telegramRetry')}
        </button>
      </div>
    );
  }

  // ── WAITING: 3-zone desktop layout + mobile single button ────────────────
  return (
    <div className="space-y-4">

      {/* ── MOBILE: single open button + PIN ──────────────────────────── */}
      <div className="sm:hidden space-y-3">
        <a
          href={linkData?.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex justify-center items-center gap-2 w-full px-4 py-3.5 bg-blue-500 text-white text-sm font-bold rounded-xl hover:bg-blue-600 transition-colors"
        >
          <TelegramIcon className="w-4 h-4" />
          {t('profile.telegramOpenMobile')}
        </a>

      {/* Mobile: PIN badge for manual entry */}
        <div>
          <p className="text-xs text-gray-400 mb-1.5">
            {t('profile.telegramBotName')} — {t('profile.telegramPinLabel')}
          </p>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
            <span className="flex-1 font-mono text-2xl font-extrabold tracking-[0.25em] text-slate-800 select-all">
              {formattedPin}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
            >
              {copied
                ? <><CheckCircle2 className="w-3.5 h-3.5" />{t('profile.telegramCopied')}</>
                : <><Copy className="w-3.5 h-3.5" />{t('profile.telegramCopyPin')}</>
              }
            </button>
          </div>
        </div>
      </div>

      {/* ── DESKTOP: 3-zone grid ─────────────────────────────────────── */}
      <div className="hidden sm:grid sm:grid-cols-3 sm:gap-5 sm:items-start">

        {/* Zone 1 — Open on desktop */}
        <div className="flex flex-col items-stretch">
          <p className="text-xs font-medium text-gray-400 mb-2 text-center">{t('profile.telegramZoneDesktop')}</p>
          <a
            href={linkData?.link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-3 p-5 bg-blue-500 text-white rounded-2xl hover:bg-blue-600 transition-colors text-center flex-1"
          >
            <TelegramIcon className="w-12 h-12" />
            <span className="text-sm font-bold leading-snug">{t('profile.telegramOpenDesktop')}</span>
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </a>
        </div>

        {/* Zone 2 — QR code */}
        <div className="flex flex-col items-center gap-2">
          <p className="text-xs font-medium text-gray-400 mb-2">{t('profile.telegramZoneQr')}</p>
          <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-sm">
            <QRCode
              value={linkData?.link ?? ' '}
              size={120}
              level="M"
              style={{ display: 'block' }}
            />
          </div>
          <p className="text-xs text-gray-500 font-medium">{t('profile.telegramQrScan')}</p>
        </div>

        {/* Zone 3 — PIN code */}
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-gray-400 mb-2">{t('profile.telegramZonePin')}</p>
          <p className="text-xs text-gray-500 leading-relaxed">
            {t('profile.telegramBotName')}
          </p>
          <p className="text-xs text-gray-400">{t('profile.telegramPinLabel')}</p>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
            <span className="flex-1 font-mono text-xl font-extrabold tracking-[0.2em] text-slate-800 select-all">
              {formattedPin}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              title={t('profile.telegramCopyPin')}
              className="flex-shrink-0 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              {copied
                ? <CheckCircle2 className="w-4 h-4 text-green-500" />
                : <Copy className="w-4 h-4" />
              }
            </button>
          </div>
          {copied && (
            <p className="text-xs text-green-600 font-medium">{t('profile.telegramCopied')}</p>
          )}
        </div>
      </div>

      {/* ── Polling indicator — always visible ───────────────────────── */}
      <div className="flex items-center gap-2.5 text-xs text-gray-400 pt-1">
        <WaitingDots />
        <span>{t('profile.telegramWaiting')}</span>
      </div>
    </div>
  );
};
